/**
 * useHashRoute — minimal hash-based router.
 *
 * Reads `window.location.hash` and re-renders subscribers when it
 * changes. We don't need a real router (no nested routes, no query
 * params, no auth gates), so 30 lines beats pulling in a dependency.
 *
 * Supported routes today:
 *   - `#/`        → standalone Vera (the original App)
 *   - `#/forma`   → Vera rendered inside a cosmetic Forma chrome
 *
 * Anything else falls back to standalone.
 */
import { useEffect, useState } from "preact/hooks";

export type RouteName = "standalone" | "forma";

export function parseRoute(hash: string): RouteName {
  // Strip leading "#" and trailing slash to normalise.
  const path = hash.replace(/^#/, "").replace(/^\//, "").replace(/\/$/, "");
  if (path === "forma") return "forma";
  return "standalone";
}

export function setRoute(name: RouteName): void {
  const next = name === "forma" ? "#/forma" : "#/";
  if (window.location.hash !== next) {
    window.location.hash = next;
  }
}

export function useHashRoute(): RouteName {
  const [route, setRouteState] = useState<RouteName>(() =>
    parseRoute(window.location.hash)
  );

  useEffect(() => {
    const onChange = () => setRouteState(parseRoute(window.location.hash));
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);

  return route;
}
